import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const API_BASE = 'https://www.goldapi.io/api';
const API_KEY = import.meta.env.VITE_GOLD_API_KEY;

/**
 * Get the current period number based on Egypt local time (UTC+2)
 * Period 1: 00:00 - 17:59 (Before 6 PM)
 * Period 2: 18:00 - 19:59 (6 PM - 8 PM)
 * Period 3: 20:00 - 23:59 (After 8 PM)
 * 
 * Returns: "YYYY-MM-DD-P1", "YYYY-MM-DD-P2", or "YYYY-MM-DD-P3"
 */
const getCurrentPeriodId = () => {
  // Use UTC+2 for Egypt time
  const now = new Date();
  const egyptOffset = 2 * 60; // UTC+2 in minutes
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const egyptTime = new Date(utc + egyptOffset * 60000);

  const year = egyptTime.getFullYear();
  const month = String(egyptTime.getMonth() + 1).padStart(2, '0');
  const day = String(egyptTime.getDate()).padStart(2, '0');
  const hour = egyptTime.getHours();

  let period = 1;
  if (hour >= 18 && hour < 20) period = 2;
  else if (hour >= 20) period = 3;

  return `${year}-${month}-${day}-P${period}`;
};

/**
 * Fetch gold price with Firestore smart caching.
 * - Checks Firestore for data from the current period first.
 * - If not found, fetches from the API and saves to Firestore.
 * - If API fails, returns last available data from Firestore as fallback.
 * 
 * @param {string} currency - ISO currency code (e.g., 'EGP', 'AED')
 * @returns {Promise<Object>} Gold price data
 */
export const fetchGoldPrice = async (currency) => {
  const periodId = getCurrentPeriodId();
  const docId = `${currency}_${periodId}`;
  const currentDocRef = doc(db, 'gold_prices_cache', docId);
  const latestDocRef = doc(db, 'gold_prices_cache', `${currency}_latest`);

  // --- STEP 1: Check Firestore for current period data ---
  try {
    const cachedSnap = await getDoc(currentDocRef);
    if (cachedSnap.exists()) {
      // Valid cached data found for this period — no API call needed!
      return { ...cachedSnap.data().priceData, fromCache: true };
    }
  } catch (firestoreError) {
    console.warn('Firestore read failed, will try API:', firestoreError);
  }

  // --- STEP 2: No cache for this period, try to fetch from API ---
  
  // Load "latest" document as fallback in case API fails
  let fallbackData = null;
  try {
    const latestSnap = await getDoc(latestDocRef);
    if (latestSnap.exists()) {
      fallbackData = latestSnap.data().priceData;
    }
  } catch {
    // silently ignore
  }

  if (!API_KEY || API_KEY === 'YOUR_API_KEY_HERE') {
    if (fallbackData) return { ...fallbackData, fromCache: true, isFallback: true };
    throw new Error('API_KEY_MISSING');
  }

  try {
    const response = await fetch(`${API_BASE}/XAU/${currency}`, {
      method: 'GET',
      headers: {
        'x-access-token': API_KEY,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // API limit reached or server error — use fallback
      if (fallbackData) return { ...fallbackData, fromCache: true, isFallback: true };
      throw new Error(response.status === 429 ? 'RATE_LIMITED' : `API_ERROR_${response.status}`);
    }

    const data = await response.json();

    // --- STEP 3: Save fresh data to Firestore (both period doc and latest doc) ---
    try {
      const payload = {
        priceData: data,
        currency,
        periodId,
        fetchedAt: new Date().toISOString(),
      };
      // Save for this specific period (prevents re-fetching until next period)
      await setDoc(currentDocRef, payload);
      // Also update the "latest" document (used as fallback)
      await setDoc(latestDocRef, payload);
    } catch (saveError) {
      console.warn('Failed to save to Firestore cache:', saveError);
      // Not critical — data will still be returned to the user
    }

    return { ...data, fromCache: false };

  } catch (error) {
    // Network error — use fallback from Firestore
    if (fallbackData) return { ...fallbackData, fromCache: true, isFallback: true };
    throw error;
  }
};

/**
 * No-op: Cache is now managed by Firestore, no local clearing needed.
 * Kept for backward compatibility with useGoldPrice hook.
 */
export const clearCache = () => {};
